/**
 * Phase 27.53.Q — Marketing Promo Send History dialog.
 *
 * Shows KR admins three lists:
 *   1. Scheduled     — promos queued for the future (with Cancel button)
 *   2. Sent          — audit log of broadcasts already dispatched
 *   3. Failed        — scheduled promos that couldn't be sent
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, CalendarClock, CheckCircle2, AlertTriangle, X, Trash2, History } from 'lucide-react';
import { toast } from 'sonner';
import { marketingAPI } from '@/lib/api';

const AUDIENCE_LABELS = {
  all: 'All Agents & Agencies',
  agents: 'Agents only',
  agencies: 'Agencies only',
};

function formatDT(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function PromoHistoryDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ sent: [], pending: [], failed: [] });
  const [cancellingId, setCancellingId] = useState(null);

  const load = () => {
    setLoading(true);
    marketingAPI.promoHistory()
      .then((res) => setData(res.data || { sent: [], pending: [], failed: [] }))
      .catch(() => toast.error('Could not load promo history'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const cancelScheduled = async (id) => {
    if (!window.confirm('Cancel this scheduled promo? It will not be sent.')) return;
    setCancellingId(id);
    try {
      await marketingAPI.cancelScheduledPromo(id);
      toast.success('Scheduled promo cancelled');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
    } finally {
      setCancellingId(null);
    }
  };

  const Row = ({ row, kind }) => (
    <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`promo-history-row-${row.scheduled_id || row.created_at}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900 truncate">{row.subject}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
            {kind === 'pending' && (
              <span className="text-purple-700 font-medium flex items-center gap-1">
                <CalendarClock size={11} /> Fires {formatDT(row.scheduled_at)}
              </span>
            )}
            {kind === 'sent' && (
              <>
                <span className="text-emerald-700 font-medium">Sent {formatDT(row.created_at)}</span>
                <span>· {row.sent_count} delivered{row.failed_count ? `, ${row.failed_count} failed` : ''}</span>
                {row.source === 'scheduled' && (
                  <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">Scheduled</span>
                )}
              </>
            )}
            {kind === 'failed' && (
              <span className="text-rose-700 font-medium">Failed · was scheduled for {formatDT(row.scheduled_at)}</span>
            )}
            <span>· {AUDIENCE_LABELS[row.audience] || row.audience}</span>
          </div>
          {(row.product_titles || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {row.product_titles.map((t, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
        {kind === 'pending' && (
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            onClick={() => cancelScheduled(row.scheduled_id)}
            disabled={cancellingId === row.scheduled_id}
            data-testid={`cancel-scheduled-${row.scheduled_id}`}
          >
            {cancellingId === row.scheduled_id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
          </Button>
        )}
      </div>
    </div>
  );

  const empty = (label) => (
    <div className="text-center py-10 text-sm text-slate-400 italic">{label}</div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 gap-0 flex flex-col" data-testid="promo-history-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <History size={18} className="text-purple-600" /> Promo Send History
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
              <Loader2 size={16} className="animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <Tabs defaultValue="pending">
              <TabsList className="mb-4">
                <TabsTrigger value="pending" data-testid="history-tab-pending">
                  <CalendarClock size={13} className="mr-1.5" />
                  Scheduled <span className="ml-1.5 text-xs opacity-70">({data.pending.length})</span>
                </TabsTrigger>
                <TabsTrigger value="sent" data-testid="history-tab-sent">
                  <CheckCircle2 size={13} className="mr-1.5" />
                  Sent <span className="ml-1.5 text-xs opacity-70">({data.sent.length})</span>
                </TabsTrigger>
                {data.failed.length > 0 && (
                  <TabsTrigger value="failed" data-testid="history-tab-failed">
                    <AlertTriangle size={13} className="mr-1.5" />
                    Failed <span className="ml-1.5 text-xs opacity-70">({data.failed.length})</span>
                  </TabsTrigger>
                )}
              </TabsList>
              <TabsContent value="pending">
                {data.pending.length === 0 ? empty('No promos scheduled.') : (
                  <div className="space-y-2">
                    {data.pending.map((r) => <Row key={r.scheduled_id} row={r} kind="pending" />)}
                  </div>
                )}
              </TabsContent>
              <TabsContent value="sent">
                {data.sent.length === 0 ? empty('No promo broadcasts yet.') : (
                  <div className="space-y-2">
                    {data.sent.map((r) => <Row key={r.created_at + r.subject} row={r} kind="sent" />)}
                  </div>
                )}
              </TabsContent>
              <TabsContent value="failed">
                <div className="space-y-2">
                  {data.failed.map((r) => <Row key={r.scheduled_id} row={r} kind="failed" />)}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
        <div className="flex justify-end px-6 py-3 border-t border-slate-100 bg-white shrink-0 rounded-b-lg">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X size={14} className="mr-1" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
